'use client';

import CategorySkeleton from '@c/skeletons/CategorySkeleton';
import { TabButton } from '@ui/Tabs';
import { ComponentType, Suspense, useState } from 'react';

export type LazyTabItem<P extends object> = {
  id: string;
  title: string;
  Component: ComponentType<P>;
  unreads?: number;
  preload?: () => void;
};

type UseLazyTabsProps<P extends object> = {
  tabItems: LazyTabItem<P>[];
  initial?: number;
  full?: boolean;
  sharedProps: P;
  value?: number;
  defaultValue?: number;
  onChange?: (index: number) => void;
};

export function useLazyTabs<P extends object>({
  tabItems,
  sharedProps,
  full,
  value,
  defaultValue = 0,
  onChange,
}: UseLazyTabsProps<P>) {
  const [internal, setInternal] = useState(defaultValue);

  const current = value ?? internal;

  const setCurrent = (index: number) => {
    if (value === undefined) {
      setInternal(index);
    }
    onChange?.(index);
  };

  const ActiveComponent = tabItems[current]?.Component;

  const body = (
    <Suspense fallback={<CategorySkeleton />}>
      {ActiveComponent ? <ActiveComponent {...sharedProps} /> : null}
    </Suspense>
  );

  const tabs = (
    <div className={`flex gap-x-6 p-4 w-${full ? 'full' : 'min'}`}>
      {tabItems.map((item, i) => (
        <TabButton
          key={item.id}
          title={item.title}
          isCurrent={current === i}
          onClick={() => setCurrent(i)}
          onMouseEnter={item.preload}
        />
      ))}
    </div>
  );

  return { tabs, body, current, setCurrent };
}
